import { useCallback, useMemo } from 'react';
import { isValid, min, parseISO } from 'date-fns';

import {
	useGetOnboardingState,
	deserializeState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import {
	IS_NAV_4_GLOBAL_SPOTLIGHT_DISMISSED,
	IS_NAV_4_SPACE_TOUR_DISMISSED,
	LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS,
	MODERNIZE_CONFLUENCE_IPM_MODAL,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

/**
 * Finds the earliest valid datetime from an array of potential datetime values from onboarding state
 * The values can be: Date | boolean | undefined
 *
 * @param values - Array of potential datetime values which could be:
 *                 - ISO datetime strings
 *                 - Boolean values (legacy format where true represents dismissal)
 *                 - Undefined
 * @param defaultDate - ISO datetime string to use when a boolean is encountered.
 *
 * @returns The earliest valid Date object from the provided values, the default date if `true` was encountered, or undefined if all values are invalid or undefined.
 *
 * @example
 * // Returns earliest of two dates
 * getEarliestDatetime(['2023-01-15T12:00:00Z', '2023-01-10T09:30:00Z'], '2023-01-01T00:00:00Z');
 * // returns '2023-01-10T09:30:00Z', as a Date.
 *
 * // Converts boolean true to default date
 * getEarliestDatetime([true, '2023-02-15T12:00:00Z'], '2023-01-01T00:00:00Z');
 * // returns '2023-01-01T00:00:00Z', as a Date.
 */
export const getEarliestDatetime = (
	values: (string | boolean | undefined)[],
	defaultDate: string,
): Date | undefined => {
	// Only keep expected dismissed values: either valid ISO datetimes,
	// or the legacy value `true` which we replace with a default date
	const validDates = values
		.map((value) => (value === true ? defaultDate : value))
		.filter((value): value is string => typeof value === 'string')
		.map((value) => parseISO(value))
		.filter(isValid);

	return validDates.length === 0 ? undefined : min(validDates);
};

const DEFAULT_CHANGEBOARDING_DISMISSED_DATE = '2025-06-09T00:00:00.000Z';

export enum STEPS {
	NAV_4_GLOBAL = 'nav4Global',
	NAV_4_SPACE = 'nav4Space',
	LIVE_DOC = 'liveDoc',
	CONVERT_TO_PAGE = 'convertToPage',
	CONTENT_WRAPPER = 'contentWrapper',
}

export const STEPS_ONBOARDING_STATE_KEYS = {
	[STEPS.NAV_4_GLOBAL]: IS_NAV_4_GLOBAL_SPOTLIGHT_DISMISSED,
	[STEPS.NAV_4_SPACE]: IS_NAV_4_SPACE_TOUR_DISMISSED,
	[STEPS.LIVE_DOC]: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedLiveDocTour,
	[STEPS.CONVERT_TO_PAGE]:
		LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedConvertToPageSpotlight,
	[STEPS.CONTENT_WRAPPER]:
		LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedContentWrapperTour,
};

export const useGetModernizeChangeboardingOnboardingState = () => {
	const { isLicensed, isGuest } = useAccessStatus();
	const accessError =
		isLicensed || isGuest
			? null
			: new Error('Only licensed or guest users can retrieve onboarding state');

	if (accessError) {
		markErrorAsHandled(accessError);
	}

	const { data, loading, error } = useGetOnboardingState([
		IS_NAV_4_GLOBAL_SPOTLIGHT_DISMISSED,
		IS_NAV_4_SPACE_TOUR_DISMISSED,
		...Object.values(LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS),
		...Object.values(MODERNIZE_CONFLUENCE_IPM_MODAL),
	]);

	const {
		isNav4GlobalSpotlightDismissed,
		isNav4SpaceTourDismissed,
		hasDismissedIntroSpotlight,
		hasDismissedLiveDocTour,
		hasDismissedLiveDocModal,
		hasDismissedContentWrapperTour,
		hasDismissedAiButtonTour,
		hasDismissedConvertToPageSpotlight,
		hasDismissedModernizeConfluenceIpmModal,
	} = deserializeState(data);

	const changeboardingStartedAt = useMemo(() => {
		return getEarliestDatetime(
			[
				isNav4GlobalSpotlightDismissed,
				isNav4SpaceTourDismissed,
				hasDismissedIntroSpotlight,
				hasDismissedLiveDocTour,
				hasDismissedLiveDocModal,
				hasDismissedContentWrapperTour,
				hasDismissedConvertToPageSpotlight,
			],
			DEFAULT_CHANGEBOARDING_DISMISSED_DATE,
		);
	}, [
		isNav4GlobalSpotlightDismissed,
		isNav4SpaceTourDismissed,
		hasDismissedIntroSpotlight,
		hasDismissedLiveDocTour,
		hasDismissedLiveDocModal,
		hasDismissedContentWrapperTour,
		hasDismissedConvertToPageSpotlight,
	]);

	return {
		isNav4GlobalSpotlightDismissed,
		isNav4SpaceTourDismissed,
		hasDismissedIntroSpotlight,
		hasDismissedLiveDocTour,
		hasDismissedLiveDocModal,
		hasDismissedContentWrapperTour,
		hasDismissedAiButtonTour,
		hasDismissedConvertToPageSpotlight,
		hasDismissedModernizeConfluenceIpmModal,
		changeboardingStartedAt,
		loading,
		error: error || accessError,
	};
};

export const useSetModernizeChangeboardingOnboardingState = () => {
	const { setOnboardingState } = useSetOnboardingState();

	const completeNav4GlobalSpotlight = useCallback(() => {
		setOnboardingState({
			key: IS_NAV_4_GLOBAL_SPOTLIGHT_DISMISSED,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeNav4SpaceTour = useCallback(() => {
		setOnboardingState({
			key: IS_NAV_4_SPACE_TOUR_DISMISSED,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeContentWrapperTour = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedContentWrapperTour,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeLiveDocModal = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedLiveDocModal,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeModernizeConfluenceIpmModal = useCallback(() => {
		setOnboardingState({
			key: MODERNIZE_CONFLUENCE_IPM_MODAL.HAS_DISMISSED_MODAL,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeLiveDocTour = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedLiveDocTour,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeConvertToPageSpotlight = useCallback(() => {
		setOnboardingState({
			key: STEPS_ONBOARDING_STATE_KEYS[STEPS.CONVERT_TO_PAGE],
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeSegments = useCallback(
		(steps: STEPS[], options: { dismissIpmModal?: boolean } = {}) => {
			const onboardingStateUpdates = [
				...(options.dismissIpmModal
					? [
							{
								key: MODERNIZE_CONFLUENCE_IPM_MODAL.HAS_DISMISSED_MODAL,
								value: new Date().toISOString(),
							},
							{
								key: MODERNIZE_CONFLUENCE_IPM_MODAL.HAS_DISMISSED_BANNER,
								value: new Date().toISOString(),
							},
						]
					: []),
				...steps.map((step) => ({
					key: STEPS_ONBOARDING_STATE_KEYS[step],
					value: new Date().toISOString(),
				})),
			];

			if (onboardingStateUpdates.length > 0) {
				setOnboardingState(onboardingStateUpdates);
			}
		},
		[setOnboardingState],
	);

	return {
		completeNav4GlobalSpotlight,
		completeNav4SpaceTour,
		completeLiveDocModal,
		completeLiveDocTour,
		completeContentWrapperTour,
		completeConvertToPageSpotlight,
		completeSegments,
		completeModernizeConfluenceIpmModal,
	};
};
